///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Slow orbital camera drift for idle/manual orbit mode.
///
/// With an entity focused, orbits it by advancing
/// EntityCameraState.relativeSpherical.theta.
///
/// Zoomed out to the whole globe (EarthCameraState.isGlobeView), the
/// camera circles the Earth's polar axis: longitude advances while
/// latitude, tilt and bearing hold, so the globe turns as it really does
/// and north stays up. Circling the view point there would spin the
/// globe about the view axis (its apparent axis landing at whatever
/// angle the view happened to have), which is disorienting. Over the
/// ramp the pose also eases to a north-up nadir view of the point that
/// was in the center.
///
/// Closer in, orbits the point the camera is looking at (the fulcrum:
/// where the forward ray hits the Earth), using the same rigid
/// fulcrum-rotation model as the pan gesture, so the viewed location
/// stays fixed on screen and the camera circles it at constant height.
/// A straight-down orbit would just spin the map underneath, so if the
/// camera starts below the tour tilt the orbit also eases the tilt up to
/// ~45 deg (the same fulcrum-preserving move as the 3D button), blended
/// into the same rigid rotation.
///
/// Ease in, cut out: angular velocity ramps from 0 over ~3 s when the
/// orbit starts, but stopping is instant - the automated motion and the
/// user's gesture share the same live camera state, so there is no snap.
///
/// The pose is applied with setParamsImmediate, which skips redux; an
/// orbit can run for minutes, so the pose is also published to redux at a
/// limited rate to keep pose-driven UI (compass, 2D/3D button, nearby
/// sorting) live without a persisted write every frame.
///

import { type CameraParams, EarthCameraState, type FulcrumOrbitAnchor } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { EntityCameraState } from "@/earthbrowser/datasets/EntityCameraState"
import { spherical } from "@/earthbrowser/utils/spherical"
import { lerp, trig } from "@stovokor/math/math"

/** Seconds to ramp angular velocity from 0 to full speed */
const cRampSeconds = 3

/** Tilt the camera eases toward when orbiting (matches the 3D button) */
const cOrbitTilt = 45 * trig.rad

/** Minimum seconds between redux publishes of the orbiting pose */
const cPublishIntervalSeconds = 0.1

function easeInQuad(t: number): number {
	return t * t
}

function easeInOutQuad(t: number): number {
	return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2
}

function wrapAngle(angle: number): number {
	let a = angle
	while (a > trig.pi) a -= trig.two_pi
	while (a < -trig.pi) a += trig.two_pi
	return a
}

export class OrbitBehavior {
	private cameraState: EarthCameraState
	private rampElapsed = 0
	private active = false

	/** Rigid camera frame captured when a fulcrum orbit starts */
	private anchor?: FulcrumOrbitAnchor
	/** Pose captured when a globe (polar-axis) orbit starts */
	private globeStart?: CameraParams
	/** North-up nadir pose over the starting view point the globe orbit settles into */
	private globeTarget?: CameraParams
	/** Accumulated orbit rotation (radians) */
	private angle = 0
	/** Total tilt-in applied over the ramp (0 if already at/past cOrbitTilt) */
	private tiltDelta = 0
	/** Seconds since the earth orbit began; drives the settle-in ramp
	 *  independently of rampElapsed, which may already be complete if an
	 *  entity orbit preceded the earth orbit */
	private settleElapsed = 0
	/** Seconds since the orbiting pose was last published to redux */
	private sincePublish = 0

	constructor(cameraState: EarthCameraState) {
		this.cameraState = cameraState
	}

	public get orbiting(): boolean {
		return this.active
	}

	public start(): void {
		this.active = true
		this.rampElapsed = 0
		this.sincePublish = 0
		this.resetEarthOrbit()
	}

	/** Instant stop; persists the current pose so redux stays in sync */
	public stop(): void {
		if (!this.active) return
		this.active = false
		this.resetEarthOrbit()
		if (!EntityCameraState.active) {
			this.cameraState.saveCameraState()
		}
	}

	/** Per-frame tick; dt in seconds, speed in rad/s */
	public update(dt: number, speed: number): void {
		if (!this.active) return

		this.rampElapsed += dt
		const rampProgress = Math.min(1, this.rampElapsed / cRampSeconds)
		const deltaTheta = speed * easeInQuad(rampProgress) * dt

		// Orbit the focused entity when one owns the camera
		const entityState = EntityCameraState.active
		if (entityState) {
			this.resetEarthOrbit()
			if (deltaTheta !== 0) {
				entityState.orbitBy(deltaTheta)
			}
			return
		}

		// The orbit model is chosen and its reference pose captured on the
		// first earth-orbit frame so a preceding entity focus or spring
		// settle doesn't skew it. Each frame then builds the pose from that
		// capture alone (no live-camera feedback).
		if (!this.anchor && !this.globeStart) {
			this.angle = 0
			this.settleElapsed = 0
			if (this.cameraState.isGlobeView()) {
				this.beginGlobeOrbit()
			} else {
				this.beginFulcrumOrbit()
				if (!this.anchor) return
			}
		}

		this.angle += deltaTheta
		this.settleElapsed += dt
		const settle = easeInOutQuad(Math.min(1, this.settleElapsed / cRampSeconds))

		let params: CameraParams | undefined
		if (this.globeStart) {
			params = this.globeOrbitParams(settle)
		} else if (this.anchor) {
			params = this.cameraState.orbitAboutFulcrum(this.anchor, this.angle, this.tiltDelta * settle)
		}
		if (params) {
			this.cameraState.setParamsImmediate(params)
			// setParamsImmediate leaves redux alone; publish periodically so
			// the compass and other pose-driven UI track the orbit
			this.sincePublish += dt
			if (this.sincePublish >= cPublishIntervalSeconds) {
				this.sincePublish = 0
				this.cameraState.saveCameraState()
			}
		}
	}

	private resetEarthOrbit(): void {
		this.anchor = undefined
		this.globeStart = undefined
		this.globeTarget = undefined
		this.angle = 0
	}

	private beginFulcrumOrbit(): void {
		this.anchor = this.cameraState.captureFulcrumAnchor()
		if (!this.anchor) return

		// If the view is flatter than the tour tilt, ease it up to
		// cOrbitTilt (clamped for altitude) over the ramp - equivalent
		// to pressing the 3D button, carried by the same rigid rotation.
		const currentTilt = this.cameraState.getCurrentParams().tilt
		const targetTilt = Math.min(cOrbitTilt, this.cameraState.maxTilt() - 0.001)
		this.tiltDelta = Math.max(0, targetTilt - currentTilt)
	}

	private beginGlobeOrbit(): void {
		const start = this.cameraState.getCurrentParams()
		this.globeStart = start

		// Settle to looking straight down at the point that was in the
		// center of the view, north up, so the orbit keeps that point in
		// frame instead of the (differing, when tilted) point under the camera
		const fulcrum = new spherical(EarthCameraState.fulcrumForParams(start))
		const over = new spherical(start.radius, start.theta, start.phi).wrappedToClosest(fulcrum)
		this.globeTarget = this.cameraState.createParams(start.radius, over.theta, over.phi, 0, 0)
	}

	/**
	 * Rotation about the polar axis: longitude (theta) advances by the
	 * accumulated angle; the rest of the pose eases from the captured
	 * start to the north-up target over the settle ramp. Positive theta
	 * carries the camera west, so the globe turns eastward as it really does.
	 */
	private globeOrbitParams(settle: number): CameraParams | undefined {
		const start = this.globeStart
		const target = this.globeTarget
		if (!start || !target) return undefined

		return this.cameraState.createParams(
			start.radius,
			wrapAngle(lerp(start.theta, target.theta, settle) + this.angle),
			lerp(start.phi, target.phi, settle),
			lerp(start.tilt, target.tilt, settle),
			lerp(start.bearing, target.bearing, settle)
		)
	}
}
